import { useEffect, useState } from 'react'

import { sfx } from '../../../lib/sound'
import type { GameProps } from '../GameShell'

const SYMBOLS = ['★', '●', '▲', '■', '◆', '✚', '❍', '⬟']

interface Card {
  id: number
  symbol: string
}

function deal(): Card[] {
  const cards = SYMBOLS.flatMap((symbol, i) => [
    { id: i * 2, symbol },
    { id: i * 2 + 1, symbol },
  ])
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[cards[i], cards[j]] = [cards[j], cards[i]]
  }
  return cards
}

/** Scored on moves, so the score counts down from a generous ceiling. */
const PERFECT_BONUS = 200
const MOVE_COST = 5

export default function Memory({ onGameOver, onScore }: GameProps) {
  const [cards] = useState(deal)
  const [flipped, setFlipped] = useState<number[]>([])
  const [matched, setMatched] = useState<Set<number>>(new Set())
  const [moves, setMoves] = useState(0)
  const [locked, setLocked] = useState(false)

  // Resolve a pair once two are face up.
  useEffect(() => {
    if (flipped.length !== 2) return
    setLocked(true)
    setMoves((m) => {
      const next = m + 1
      onScore?.(Math.max(0, PERFECT_BONUS - next * MOVE_COST))
      return next
    })

    const [a, b] = flipped
    const same = cards[a].symbol === cards[b].symbol

    const timer = setTimeout(
      () => {
        if (same) {
          sfx.pop()
          setMatched((current) => new Set([...current, a, b]))
        } else {
          sfx.tick()
        }
        setFlipped([])
        setLocked(false)
      },
      same ? 300 : 700,
    )
    return () => clearTimeout(timer)
  }, [flipped, cards, onScore])

  // Finish once every card is paired.
  useEffect(() => {
    if (matched.size > 0 && matched.size === cards.length) {
      sfx.complete()
      onGameOver(Math.max(0, PERFECT_BONUS - moves * MOVE_COST))
    }
  }, [matched, cards.length, moves, onGameOver])

  function flip(index: number) {
    if (locked || flipped.includes(index) || matched.has(index)) return
    setFlipped((current) => [...current, index])
  }

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted">
        {moves} {moves === 1 ? 'move' : 'moves'} · fewer is better
      </p>
      <div className="mx-auto grid max-w-md grid-cols-4 gap-2">
        {cards.map((card, index) => {
          const showing = flipped.includes(index) || matched.has(index)
          return (
            <button
              key={card.id}
              onClick={() => flip(index)}
              className={`grid aspect-square place-items-center rounded-xl border text-3xl
                transition-all duration-200 ${
                  matched.has(index)
                    ? 'border-transparent bg-positive/10 text-positive'
                    : showing
                      ? 'border-brand bg-brand-soft text-brand'
                      : 'border-border-subtle bg-surface text-transparent hover:border-brand'
                }`}
            >
              {showing ? card.symbol : '?'}
            </button>
          )
        })}
      </div>
    </div>
  )
}
